import React, { useState, useEffect } from "react";
import Layout from "../../Components/Layout";
import Styles from "./index.module.css";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { Services } from "../../Services";
import ReactLoading from "react-loading";
import { warningSwal } from "../../Util/Toast";
import Table from "./Table";
import Select from "react-select";
import moment from "moment";
import ReactPaginate from "react-paginate";

function BatchFinishedReport() {
    const { register, handleSubmit, setValue, watch, formState: { errors }, getValues } = useForm();
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [link, setLink] = useState("");
    const [tutorOptions, setTutorOptions] = useState([]);
    const [tutorSearch, setTutorSearch] = useState("");
    const [selectedTutor, setSelectedTutor] = useState(null);
    const [tutorSearchLoad, setTutorSearchLoad] = useState(false);
    const [totalRecords, setTotalRecords] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const token = { Authorization: `token ${localStorage.getItem("token")}` };

    useEffect(() => {
        const debouncedSearch = setTimeout(async () => {
            if (typeof tutorSearch === "string" && tutorSearch.length > 0) {
                setTutorSearchLoad(true);
                try {
                    const data = await Services.searchActiveTutor(
                        "POST",
                        JSON.stringify({ SearchString: tutorSearch }),
                        token
                    );
                    const formattedOptions = data.Tutors?.filter(
                        (item) => item.TutorStatus == 1
                    )?.map((tutor) => ({
                        label: `${tutor.FirstName} ${tutor.LastName}`,
                        value: tutor.TutorID,
                    }));
                    setTutorOptions(formattedOptions || []);
                } catch (error) {
                    console.error("Error fetching tutors:", error);
                    setTutorOptions([]);
                } finally {
                    setTutorSearchLoad(false);
                }
            } else {
                setTutorOptions([]);
            }
        }, 350);

        return () => clearTimeout(debouncedSearch);
    }, [tutorSearch]);

    const onSubmit = async (data, pageNumber = 1) => {
        const TutorID = selectedTutor?.value || null;

        if (!data.FromDate || !data.ToDate) {
            warningSwal("Warning", "From Date and To Date are required");
            return;
        }

        if (data.FromDate > data.ToDate) {
            warningSwal("Warning", "Please select a valid date range");
            return;
        }

        const start = moment(data.FromDate);
        const end = moment(data.ToDate);
        if (end.diff(start, 'days') > 15) {
            warningSwal("Warning", "Date range cannot exceed 15 days");
            return;
        }

        setLoading(true);
        try {
            const res = await Services.get_finished_batches(
                "GET",
                null,
                token,
                data.FromDate || null,
                data.ToDate || null,
                data.Status || "all",
                TutorID,
                pageNumber,
                20
            );
            setLoading(false);
            if (res?.Status === 1) {
                setTotalRecords(res.TotalRecords);
                setTotalPages(res.Pagination?.TotalPages || 0);
                setPage(res.Pagination?.CurrentPage || 1);
                setResults(
                    res.Results?.map((item) => ({
                        "Parent Full Name": item.ParentFullName || "NA",
                        "Student Full Name": item.StudentFullName || "NA",
                        "Phone Number": item.PhoneNumber || "NA",
                        "Batch Name": item.BatchName,
                        "Teacher Name": item.TeacherName,
                        "RM Name": item.RMName,
                        "Created By": item.CreatedBy,
                        "Finished On": item.FinishedOn && item.FinishedOn !== "0000-00-00" ? moment(item.FinishedOn).format("DD-MM-YYYY") : "NA",
                        "First Completed Class": item.FirstCompletedClassDate && item.FirstCompletedClassDate !== "0000-00-00 00:00:00" ? moment.utc(item.FirstCompletedClassDate).local().format("DD-MM-YYYY HH:mm:ss") : "NA",
                        "Last Completed Class": item.LastCompletedClassDate && item.LastCompletedClassDate !== "0000-00-00 00:00:00" ? moment.utc(item.LastCompletedClassDate).local().format("DD-MM-YYYY HH:mm:ss") : "NA",
                        "Last Completed File": (() => {
                            let filesTaken = "";
                            let fileNum = "";

                            let parsedFiles = item.LastCompletedFileTaken;
                            if (typeof parsedFiles === 'string') {
                                try { parsedFiles = JSON.parse(parsedFiles); } catch (e) { }
                            }

                            let cleanArr = [];
                            if (Array.isArray(parsedFiles)) {
                                cleanArr = parsedFiles
                                    .map(x => typeof x === 'string' ? x.trim() : x)
                                    .filter(x => x !== null && x !== undefined && x !== "" && String(x).toLowerCase() !== "null");
                            } else if (parsedFiles && typeof parsedFiles !== 'object') {
                                let str = String(parsedFiles).trim();
                                if (str.startsWith("[") && str.endsWith("]")) {
                                    str = str.slice(1, -1);
                                }
                                cleanArr = str
                                    .split(",")
                                    .map(x => x.trim())
                                    .filter(x => x !== "" && x.toLowerCase() !== "null");
                            }
                            filesTaken = cleanArr.join(", ");

                            let parsedNum = item.LastFileNumber;
                            if (typeof parsedNum === 'string') {
                                try { parsedNum = JSON.parse(parsedNum); } catch (e) { }
                            }
                            if (parsedNum && typeof parsedNum === 'object' && !Array.isArray(parsedNum)) {
                                const lp = parsedNum.LessonPlan;
                                const slide = parsedNum.Slide;
                                if (lp || slide) {
                                    fileNum = `${lp || ''} ${slide ? `(${slide})` : ''}`.trim();
                                }
                            }

                            if (filesTaken && fileNum) return `${filesTaken}, ${fileNum}`;
                            if (filesTaken) return filesTaken;
                            if (fileNum) return fileNum;
                            return "NA";
                        })(),
                        "Batch Status": item.BatchStatus,
                        "Completion Reason": item.CompletionReason,
                    })) || []
                );
            } else {
                console.error(res);
                warningSwal("Warning", res?.message || "Failed to fetch report");
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            warningSwal("Warning", "Something went wrong");
        }
    };

    const handlePageClick = (data) => {
        const selectedPage = data.selected + 1;
        setPage(selectedPage);
        const formData = getValues();
        onSubmit(formData, selectedPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleExport = async () => {
        const data = getValues();
        const TutorID = selectedTutor?.value || null;
        if (!data.FromDate || !data.ToDate) {
            warningSwal("Warning", "From Date and To Date are required");
            return;
        }
        if (data.FromDate > data.ToDate) {
            warningSwal("Warning", "Please select a valid date range");
            return;
        }
        setLoading(true);
        try {
            const res = await Services.batch_finished_report(
                "GET",
                null,
                token,
                data.FromDate || null,
                data.ToDate || null,
                data.Status || "all",
                TutorID
            );
            setLoading(false);
            if (res?.Status === 1 && res.CsvFile) {
                window.open(res.CsvFile, "_blank");
            } else {
                console.error(res);
                warningSwal("Warning", res?.message || "Failed to generate export");
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            warningSwal("Warning", "Something went wrong during export");
        }
    };

    return (
        <Layout Active={"Batch Finish Report"}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Batch Finished Report</title>
            </Helmet>
            <div className={Styles["Container"]}>
                <div className={Styles["wrapper"]}>
                    <h3>Batch Finished Report</h3>
                    <form onSubmit={handleSubmit((data) => onSubmit(data, 1))}>
                        <div className={Styles["form-control"]} style={{ flex: 2.5, minWidth: '250px' }}>
                            <label>Teacher <span className="important">*</span></label>
                            <div style={{ width: '100%' }}>
                                <Select
                                    options={tutorOptions}
                                    onInputChange={(val) => setTutorSearch(val)}
                                    onChange={(val) => setSelectedTutor(val)}
                                    value={selectedTutor}
                                    isClearable
                                    placeholder="Search teacher by name"
                                    isLoading={tutorSearchLoad}
                                />
                            </div>
                        </div>

                        <div className={Styles["form-control"]} style={{ flex: 1, minWidth: '130px' }}>
                            <label>From Date <span className="important">*</span></label>
                            <input type="date" {...register("FromDate")} />
                            {errors.FromDate && <span style={{ color: 'red', fontSize: '12px' }}>From Date is required</span>}
                        </div>
                        <div className={Styles["form-control"]} style={{ flex: 1, minWidth: '130px' }}>
                            <label>To Date <span className="important">*</span></label>
                            <input type="date" {...register("ToDate")} />
                            {errors.ToDate && <span style={{ color: 'red', fontSize: '12px' }}>To Date is required</span>}
                        </div>
                        <div className={Styles["form-control"]} style={{ flex: 1, minWidth: '150px' }}>
                            <label>Status</label>
                            <select {...register("Status")} defaultValue={"all"}>
                                <option value="all">All</option>
                                <option value="4">Gone Cold</option>
                                <option value="3">Course Completed</option>
                                <option value="5">Batch Dissolved</option>
                                <option value="6">Parent discontinued</option>
                                <option value="7">On Hold</option>
                            </select>
                        </div>

                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`${Styles.btn} ${Styles.btnPrimary}`}
                                style={{ alignSelf: "flex-end", marginBottom: errors.FromDate || errors.ToDate ? "20px" : "0" }}
                                aria-busy={loading}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                    {loading && <ReactLoading type="spokes" color="#0077b6" height={14} width={14} />}
                                    {loading ? "Submitting..." : "Submit"}
                                </span>
                            </button>

                            {results && (
                                <button
                                    type="button"
                                    className={`${Styles.btn} ${Styles.btnExport}`}
                                    style={{ alignSelf: "flex-end", marginBottom: errors.FromDate || errors.ToDate ? "20px" : "0" }}
                                    onClick={handleExport}
                                    disabled={loading}>
                                    Export
                                </button>
                            )}
                        </div>
                    </form>

                    {results && (
                        <div style={{ overflow: "auto", marginTop: 16 }}>
                            {totalRecords !== null && totalRecords !== undefined && (
                                <div style={{ marginBottom: "10px", fontSize: "16px", fontWeight: "bold", color: "#0077b6" }}>
                                    Total Records: {totalRecords}
                                </div>
                            )}
                            <Table
                                data={results}
                                emptyHeaders={[
                                    "Parent Full Name",
                                    "Student Full Name",
                                    "Phone Number",
                                    "Batch Name",
                                    "Teacher Name",
                                    "RM Name",
                                    "Created By",
                                    "Finished On",
                                    "First Completed Class",
                                    "Last Completed Class",
                                    "Last Completed File Number",
                                    "Batch Status",
                                    "Completion Reason"
                                ]}
                                style={{ minWidth: "1500px" }}
                                emptyMessage="No record found"
                            />
                            {totalPages > 1 && (
                                <ReactPaginate
                                    previousLabel={"<"}
                                    nextLabel={">"}
                                    breakLabel={"..."}
                                    pageCount={totalPages}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={3}
                                    onPageChange={handlePageClick}
                                    containerClassName={"pagination"}
                                    activeClassName={"active"}
                                    forcePage={page - 1}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default BatchFinishedReport;
